// mapUiConfig — DEKLARATIVER Katalog der Karten-Bedien-UI (Phase Map-UI). Slice 1: nur die MapToolsMenu-Group.
//
// Zwei Ebenen: `groups` (eigenständige Controls an einer ControlPosition) mit `kind`:
//   - 'buttonStack' → vertikale Button-Leiste (MapToolsMenu) mit `items`
//   - 'module'      → eigenständiges Modul (OV-Panel, multiLocation) — kommt in Slice 2
// `contexts` steuert, in welchen Profilen (main/gallery/…) Group bzw. Item erscheinen.
// Actions/Toggles bekommen eine schlanke `api` (vom Controller) — so bleibt der Katalog Daten, die Verdrahtung außen.

import { ICON_FURTHER_ARCHIVES, ICON_FIT_TO_VIEW, ICON_SEARCH_CIRCLE, ICON_CLEAR_CIRCLE } from "./MapToolsMenu.js";
import { createMultiLocationModule } from "./multiLocationControl.js";
import { createMapTypeControlModule } from "./mapTypeControl.js";
import { createZoomControlModule } from "./zoomControl.js";
import { createTiltRotateControlModule } from "./tiltRotateControl.js";
import { createOverlayPanel } from "../overlay/OverlayPanelController.js";

export const MAP_UI_CONFIG = {
    groups: [
        {
            key: 'mapTools',
            kind: 'buttonStack',
            position: 'LEFT_BOTTOM',
            contexts: ['main', 'gallery'],
            items: [
                {
                    // Chef-Wunsch (4): „Suchkreis löschen" — ÜBER dem Suchkreis-Button (erstes Item = oben im Stack),
                    // nur sichtbar wenn ein Kreis gezogen ist (visibleFromState).
                    key: 'searchcircle-clear',
                    icon: ICON_CLEAR_CIRCLE,
                    title: 'MAP_TOOL_CLEAR_CIRCLE',   // Suchkreis löschen
                    contexts: ['main'],
                    visibleFromState: 'searchCircleActive',
                    onClick: (api) => api.clearSearchCircle()
                },
                {
                    key: 'searchcircle',
                    icon: ICON_SEARCH_CIRCLE,
                    title: 'MAP_TOOL_SEARCHCIRCLE',   // Suchkreis ziehen — Linksklick (Rechtsklick geht immer) — vom Reconciler via str() aufgelöst
                    toggle: true,
                    contexts: ['main'],   // Suchkreis ist Hauptkarten-Feature
                    activeFromState: 'searchCircleMode',   // Ring spiegelt den Store (#1)
                    onToggle: (active, api) => api.setSearchCircleLeftClick(active),
                    onContextHide: (api) => api.setSearchCircleLeftClick(false)   // in Gallery: Modus aus
                },
                {
                    key: 'archives',
                    icon: ICON_FURTHER_ARCHIVES,
                    title: 'ARCHIVES_MORE',   // weitere Topotheken — bestehender IF-String (wiederverwendet, vgl. MapController.createArchiveToggle)
                    toggle: true,
                    contexts: ['main'],   // Chef-Wunsch (1): in der Galerie raus
                    size: 'small',        // Chef 5/14: kleine Variante (im Pair zu Quadrat überschrieben)
                    pair: 'archives-center',   // Chef 5: mit center in EINEM Slot — .topo2-btn-pair macht Quadrate
                    activeFromState: 'archivesShown',   // Ring spiegelt den Store (#1) — write-through über marker:archive:show
                    onToggle: (active, api) => api.publish('marker:archive:show', { checked: active })
                },
                {
                    key: 'center',
                    icon: ICON_FIT_TO_VIEW,
                    title: 'MAP_TOOL_CENTER',   // zentrieren — vom Reconciler via str() aufgelöst
                    contexts: ['main', 'gallery'],
                    size: 'small',        // Chef 5/14: kleine Variante; Galerie (allein, kein Pair) → normaler 30×30-Button wie OV
                    pair: 'archives-center',   // Chef 5: mit archives in EINEM Slot (Galerie: archives fehlt → kein Pair, center steht normal)
                    onClick: (api) => api.isGallery()
                        ? api.publish('map:fit:gallery', { index: null })
                        : api.publish('map:fit:main', {})
                },
                {
                    // Map-UI-Unify (Chef 16): OV-Panel-Auslöser im Galerie-Tools-Stack, DIREKT unter „zentrieren"
                    // (nach center = nächstes Item = darunter). Toggelt das noButton-Panel über die api.
                    key: 'overlays',
                    label: 'OVERLAYS',   // Chef-Charge 3: Text-Button (bar) statt OV-Icon; OVERLAYS = bestehender IF-String (via str())
                    title: 'OVERLAY_CONTROLS_BUTTON',   // Overlays
                    contexts: ['gallery'],
                    size: 'small',   // Chef 14
                    visibleFromState: 'overlays',   // Button nur, wenn es (veröffentlichte+platzierte) Overlays gibt — sonst leeres Panel
                    onClick: (api) => api.toggleOverlayPanel()
                }
            ]
        },
        {
            // Map-UI-Unify (Chef 16): in Main bleibt der OV-Button oben rechts („wie jetzt"), jetzt aber als normaler
            // Katalog-Button (icon-outline wie die anderen) statt eigenständiges Control. Gleiche Aktion wie das Galerie-Item.
            key: 'overlayButton',
            kind: 'buttonStack',
            position: 'TOP_RIGHT',
            contexts: ['main', 'locate', 'settings'],   // Top-right-Auslöser: Main + Admin-Karten (Gallery hat eigenen im mapTools)
            items: [
                {
                    key: 'overlays',
                    label: 'OVERLAYS',   // Chef-Charge 3: Text-Button (bar) statt OV-Icon; OVERLAYS = bestehender IF-String (via str())
                    title: 'OVERLAY_CONTROLS_BUTTON',   // Overlays
                    contexts: ['main', 'locate', 'settings'],
                    size: 'small',   // Chef 14
                    visibleFromState: 'overlays',   // Button nur, wenn es (veröffentlichte+platzierte) Overlays gibt — sonst leeres Panel
                    onClick: (api) => api.toggleOverlayPanel()
                }
            ]
        },
        {
            // Zoom +/− (RIGHT_BOTTOM): Main UND Gallery. ZUERST im Katalog → zuerst gepusht → unterster Stack = fester Anker.
            key: 'zoom',
            kind: 'module',
            position: 'RIGHT_BOTTOM',
            contexts: '*',   // universell: Zoom auf allen Karten gleich (main/gallery/locate/settings)
            factory: createZoomControlModule
        },
        {
            // Neigen + Rotation (RIGHT_BOTTOM, eigener Stack): NACH 'zoom' → wird ÜBER den Zoom-Buttons gestapelt.
            // UX-kritisch: das Menü blendet zoom-abhängig ein/aus — über zoom bleiben die Zoom-Buttons dabei ORTSFEST
            // (man trifft beim Weiterzoomen denselben Button). Ersetzt Googles rotateControl.
            key: 'tiltRotate',
            kind: 'module',
            position: 'RIGHT_BOTTOM',
            contexts: '*',   // universell: Neigen/Drehen auf allen Karten gleich (logisch ein Nav-Cluster mit zoom, technisch eigener Stack)
            factory: createTiltRotateControlModule
        },
        {
            // Karte/Satellit-Umschalter (custom topo2-Control, ersetzt Googles mapTypeControl): Main UND Gallery, oben links.
            // Modul schaltet Googles Switcher lokal ab (map.setOptions) und spiegelt den aktiven Typ via maptypeid_changed.
            key: 'mapType',
            kind: 'module',
            position: 'TOP_LEFT',
            contexts: '*',   // universell: Karte/Satellit-Umschalter oben links auf allen Karten (ersetzt Googles mapTypeControl)
            factory: createMapTypeControlModule
        },
        {
            // Galerie-Navigation (◀ index/N ▶), unten zentriert; nur Gallery, nur bei >1 Marker (Factory liefert sonst null).
            // Frischer Aufbau bei neuer Galerie via reconciler.invalidateModule('multiLocation') am listenOverlayReady.
            key: 'multiLocation',
            kind: 'module',
            position: 'BOTTOM_CENTER',
            contexts: ['gallery'],
            factory: createMultiLocationModule
        },
        {
            // OV-Panel (Button + Liste/Auge/Tools): in Main UND Gallery. Da in beiden Kontexten → kein Auto-Rebuild;
            // switchTo invalidiert → frischer Build = Reset (visible/opacity „aus") → fixt den gallery→main-Leak.
            // ovSide kontextabhängig (Gallery links, Main rechts). Panel setzt sich selbst (Button TOP_RIGHT + body) → keine position.
            key: 'overlayPanel',
            kind: 'module',
            contexts: '*',   // Panel überall; nur der Auslöser unterscheidet sich (main/locate/settings: overlayButton, gallery: mapTools)
            factory: ({ map, api, context, google }) => createOverlayPanel(google, map, {
                noButton: true,   // Map-UI-Unify (Chef 16): Auslöser ist IMMER ein Katalog-Button (main: overlayButton, gallery: mapTools)
                ovSide: context === 'gallery' ? 'left' : 'right',
                showAll: context === 'locate' || context === 'settings',   // Admin-Karten: ALLE Overlays (auch unveröffentlichte)
                store: (api && api.getStore) ? api.getStore(context) : undefined,   // per-Kontext-Store (Slice 4b)
                openGallery: (api && api.openGallery) ? api.openGallery : undefined   // {id}-Doc-Link im Infotext → Gallery (Frontend; im Admin undefined → no-op)
            })
        }
    ]
};
